import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { IList, tuple } from 'annotation-tools/utils';
import React, { FC, memo, MouseEvent, useMemo } from 'react';
import styled from 'styled-components';
import { colorDef } from '../../../constants/styles/color-def';
import { viewTexts } from '../../../constants/view-texts';
import { IKoumutenType } from '../../../types/record/koumuten';
import { BreadcrumbsJp } from '../../breadcrumbs/breadcrumbs';
import { CreateNewKoumutenDialog } from '../../dialogs/create-new-koumuten-dialog/create-new-koumuten-dialog';
import { DeleteKoumutenDialog } from '../../dialogs/delete-koumuten-dialog/delete-koumuten-dialog';
import { EditKoumutenDialog } from '../../dialogs/edit-koumuten-dialog/edit-koumuten-dialog';
import { MuiButton } from '../../shared/form-element';
import { SearchBar } from '../../shared/form-element/mui-search-bar';
import { MuiPaper } from '../../shared/mui/mui-paper';
import { MuiTablePagination } from '../../shared/mui/mui-table-pagination';
import { MuiTableRow } from '../../shared/mui/mui-table-row';
import { H2 } from '../../shared/styled/heading';
import { Root } from '../../shared/styled/root';

const vt = viewTexts.pages.koumutenList;

const CreateButtonWrapper = styled.div`
  padding: 10px;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ButtonWrapper = styled.div`
  padding: 0 5px;
  display: inline-block;
`;

interface Props {
  searchString: string;
  onSearchStringChange: (value: string) => void;
  page: number;
  onPageChange: (value: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: number) => void;
  numRows: number;
  koumutenListFiltered: IList<IKoumutenType>;
  koumutenOnClick: (id: number) => void;
  createNewKoumutenOnClick: () => void;
  createNewKoumutenDialogOpen: boolean;
  createNewKoumutenDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  editKoumutenOnClick: (koumuten: IKoumutenType) => void;
  editKoumutenDialogIsOpen: boolean;
  editKoumutenDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  koumutenToEdit: IKoumutenType | undefined;
  deleteKoumutenOnClick: (koumuten: IKoumutenType) => void;
  deleteKoumutenDialogIsOpen: boolean;
  deleteKoumutenDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  koumutenToDelete: IKoumutenType | undefined;
}

export const KoumutenListView: FC<Props> = memo(
  ({
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    onRowsPerPageChange,
    numRows,
    koumutenListFiltered,
    koumutenOnClick,
    createNewKoumutenOnClick,
    createNewKoumutenDialogOpen,
    createNewKoumutenDialogOnClose,
    editKoumutenOnClick,
    editKoumutenDialogIsOpen,
    editKoumutenDialogOnClose,
    koumutenToEdit,
    deleteKoumutenOnClick,
    deleteKoumutenDialogIsOpen,
    deleteKoumutenDialogOnClose,
    koumutenToDelete
  }: Readonly<Props>) => {
    const koumutenListWithHandler = useMemo(
      () =>
        koumutenListFiltered.map(koumuten =>
          tuple(
            {
              id: koumuten.id,
              name: koumuten.name,
              numCompanies: koumuten.numCompanies,
              author: koumuten.author,
              dateCreatedString: new Date(
                koumuten.dateCreated
              ).toLocaleString(),
              dateUpdatedString: new Date(
                koumuten.dateUpdated
              ).toLocaleString(),
              canBeDeleted: koumuten.canBeDeleted
            },
            () => koumutenOnClick(koumuten.id),
            (event: MouseEvent) => {
              event.stopPropagation();
              editKoumutenOnClick(koumuten);
            },
            (event: MouseEvent) => {
              event.stopPropagation();
              deleteKoumutenOnClick(koumuten);
            }
          )
        ),
      [
        koumutenListFiltered,
        koumutenOnClick,
        editKoumutenOnClick,
        deleteKoumutenOnClick
      ]
    );

    return (
      <Root>
        <BreadcrumbsJp />

        <H2>{vt.title}</H2>
        <SearchBarWrapper>
          <SearchBar
            placeholder={vt.searchBarPlaceholder}
            value={searchString}
            onInput={onSearchStringChange}
          />
          <CreateButtonWrapper>
            <MuiButton
              label={vt.buttons.create}
              onClick={createNewKoumutenOnClick}
            />
          </CreateButtonWrapper>
        </SearchBarWrapper>

        <MuiPaper bgColor={colorDef.paperLighter} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{vt.header.name}</TableCell>
                <TableCell>{vt.header.numCompanies}</TableCell>
                <TableCell>{vt.header.author}</TableCell>
                <TableCell>{vt.header.dateCreated}</TableCell>
                <TableCell>{vt.header.dateUpdated}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {koumutenListWithHandler.map(
                ([
                  koumuten,
                  rowOnClickHandler,
                  editButtonOnClickHandler,
                  deleteButtonOnClickHandler
                ]) => (
                  <MuiTableRow key={koumuten.id} onClick={rowOnClickHandler}>
                    <TableCell>{koumuten.name}</TableCell>
                    <TableCell>{koumuten.numCompanies}</TableCell>
                    <TableCell>{koumuten.author}</TableCell>
                    <TableCell>{koumuten.dateCreatedString}</TableCell>
                    <TableCell>{koumuten.dateUpdatedString}</TableCell>
                    <TableCell align='right'>
                      <ButtonWrapper>
                        <MuiButton
                          label={vt.buttons.edit}
                          onClick={editButtonOnClickHandler}
                        />
                      </ButtonWrapper>
                      <ButtonWrapper
                        title={
                          koumuten.canBeDeleted
                            ? undefined
                            : viewTexts.undeletableReason.koumuten
                        }
                      >
                        <MuiButton
                          color='secondary'
                          label={vt.buttons.delete}
                          onClick={deleteButtonOnClickHandler}
                          disabled={!koumuten.canBeDeleted}
                        />
                      </ButtonWrapper>
                    </TableCell>
                  </MuiTableRow>
                )
              )}
            </TableBody>
          </Table>
          <MuiTablePagination
            numRows={numRows}
            page={page}
            onChangePage={onPageChange}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        </MuiPaper>
        <CreateNewKoumutenDialog
          open={createNewKoumutenDialogOpen}
          onClose={createNewKoumutenDialogOnClose}
        />
        {koumutenToEdit !== undefined ? (
          <EditKoumutenDialog
            open={editKoumutenDialogIsOpen}
            onClose={editKoumutenDialogOnClose}
            koumuten={koumutenToEdit}
          />
        ) : (
          undefined
        )}
        {koumutenToDelete !== undefined ? (
          <DeleteKoumutenDialog
            open={deleteKoumutenDialogIsOpen}
            onClose={deleteKoumutenDialogOnClose}
            koumuten={koumutenToDelete}
          />
        ) : (
          undefined
        )}
      </Root>
    );
  }
);

KoumutenListView.displayName = 'KoumutenListView';
